//*************************************//
// Consulta de indicadores económicos  //
//*************************************//
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Stack, Card, CardContent, Typography } from "@mui/material";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.css';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import NoData from "./NoData.jsx";
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { getCustomTheme } from "../../theme/customTheme";  // Tema

const Indicadores = ({ setTitle, isSidebarOpen, modo }) => {
  const apiUrl = process.env.REACT_APP_API_URL;

  // Uso del Tema //
  const themeBase = useTheme();
  const customTheme = getCustomTheme(themeBase, modo);
  const isDark = modo === "dark";

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [indicadores, setIndicadores] = useState(null);

  // tamaño de la pantalla //
  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  // Cambio de titulo //
  useEffect(() => {
    setTitle(' Indicadores Económicos');
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setTitle]);

  const availableWidth = isSidebarOpen ? windowWidth - 220 : windowWidth;

  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp >= currentTime;
    } catch (error) {
      return false;
    }
  };

  const handleTokenExpiration = useCallback(() => {
    Swal.fire({
      icon: 'error',
      title: 'Sesión expirada',
      text: 'El token ha expirado. Por favor, inicia sesión nuevamente.',
      footer: '▶️ Overwatch',
      showConfirmButton: false,
      background: 'rgba(30, 41, 59, 0.9)',
      color: '#f8fafc',
      timer: 1500
    });
    sessionStorage.removeItem('authToken');
    setTimeout(() => {
      window.location.href = '/login';
    }, 1600);
  }, []);

  // 🔑 Obtener indicadores desde backend
  const fetchIndicadores = useCallback(async () => {
    // Obtener el token desde sessionStorage //
    const token = sessionStorage.getItem('authToken');

    if (!token || !isTokenValid(token)) {
      handleTokenExpiration(); // Manejar la expiración del token y redirigir
      return; // Evitar continuar con el código si el token es inválido o expirado
    }

    try {
      const response = await axios.post(`${apiUrl}/indicadores/getindicadores`, 
        {},
        {
        headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setIndicadores(response.data.data);
      } else {
        Swal.fire({
          icon: 'info',
          title: 'Aviso',
          text: 'Los indicadores Económicos no estan disponibles',
          footer: '▶️ Overwatch',
          allowOutsideClick: false,  // 👈 evita cierre con click afuera
          background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
          color: '#f8fafc',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-text'
          }
        });
      }
    } catch (error) {
      console.error("❌ Error en fetchIndicadores:", error);

      Swal.fire({
        icon: 'error',
        title: 'Indicadores Económicos',
        text: 'No fue posible obtener los Indicadores Económicos. Por favor, intente más tarde.',
        footer: '▶️ Overwatch',
        allowOutsideClick: false,  // 👈 evita cierre con click afuera
        background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
        color: '#f8fafc',
        customClass: {
          popup: 'swal-custom-popup',
          title: 'swal-custom-title',
          htmlContainer: 'swal-custom-text'
        }
      });
    }
  }, [apiUrl, handleTokenExpiration]);

  useEffect(() => {
    fetchIndicadores();
  }, [fetchIndicadores]);

  // 🔎 Función para mostrar valor con formato distinto si es porcentaje o pesos
  const formatValue = (valor, unidad) => {
    if (unidad === "Porcentaje") {
      return `${valor.toFixed(2)}%`;
    }
    return `$${valor.toLocaleString("es-CL")}`;
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 2,
          mt: 2,
          maxWidth: '98%',
          width: availableWidth, // Ajuste dinámico según sidebar
          overflowX: 'auto',
        }}
      >
        <ThemeProvider theme={customTheme}>
          <Stack direction="column" alignItems="center" spacing={2} sx={{ width: "100%" }}>
            {/* ---- TEMPLATE DEL RENDER ---- */}
            {[
              ["uf", "dolar", "euro"],
              ["ipc", "utm", "imacec"],
            ].map((grupo, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 2,
                  width: "100%",
                  mt: 1,
                }}
              >
                {indicadores ? (
                  grupo.map((key) => {
                    const indicador = indicadores[key];

                    return (
                      <Card
                        key={key}
                        sx={{
                          width: 220,
                          textAlign: "center",
                          backgroundColor: isDark ? "#1E293B" : "#e8f5e9",          // fondo azul grisáceo oscuro / azul claro
                          border: isDark ? "1px solid #3B82F6" : "1px solid #90CAF9",
                          boxShadow: isDark
                            ? "0 0 8px rgba(59, 130, 246, 0.4)"                    // brillo azul en modo oscuro
                            : "0 0 6px rgba(0, 0, 0, 0.15)",                       // sombra suave modo claro
                          borderRadius: 2,
                          transition: "all 0.3s ease",

                        }}
                      >
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {indicador.nombre}
                          </Typography>

                          <Typography variant="subtitle2" color="text.secondary">
                            {indicador.unidad_medida}
                          </Typography>

                          <Typography
                            variant="h5"
                            sx={{ mt: 1, fontWeight: "bold", color: "primary.main" }}
                          >
                            {formatValue(indicador.valor, indicador.unidad_medida)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {new Date(indicador.fecha).toLocaleDateString("es-CL")}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <NoData mensaje="No hay indicadores económicos" />
                )}
              </Box>
            ))}
          </Stack>
        </ThemeProvider>
      </Box>
    </div>
  );
};

export default Indicadores;
